/**
 * Core course type definitions
 */

/** A course option shown in prerequisite / co-requisite picker modals (plan + catalog). */
export interface RequisiteCourseOption {
  id: string;
  name: string;
  code: string;
  credits: number;
  grades?: number[];
}

/**
 * Legacy Course type used by student features and mock data.
 * Kept for backward compatibility with existing student dashboard.
 */
export interface LegacyCourse {
  id: string;
  code: string;
  name: string;
  description: string;
  credits: number;
  department: string;
  level: CourseLevel;
  prerequisites: string[];
  terms: Term[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  /** Available grade levels for this course (from plan-of-study data) */
  grades?: number[];
  /** Course status (e.g. 'ACTIVE', 'INACTIVE') */
  status?: string;
  /** True when the student has not met at least one prerequisite for this course */
  missingPrereq?: boolean;
  /** True when the student has not met at least one co-requisite for this course */
  missingCoreq?: boolean;
  /** Plain-text prerequisite description from the catalog */
  prerequisiteText?: string;
  /** Structured prerequisite course groups (OR of AND lists) */
  coursePrerequisites?: RequisiteCourseOption[][];
  /** Structured co-requisite course groups (OR of AND lists) */
  courseCorequisites?: RequisiteCourseOption[][];
  /** True when the same course can be taken for credit more than once */
  repeatable?: boolean;
  /** Catalog GUID from plan API (optional). validate-course-requisites uses `id` (itemName), not this field. */
  courseGuid?: string;
  /** Stable catalog row id when `id` is a plan-scoped row id (IFAL requisite lookup). */
  originalRecordId?: string;
  /** Plan instance key for IFAL list rows (list1 vs list2 OR arms share catalog `id`). */
  statementUnique?: string;
  /** Legacy equivalent-import hint when another equivalent course is already in the plan. */
  equivalentCourseLabel?: string;
}

/**
 * Legacy response type for student features
 */
export interface LegacyCoursesResponse {
  courses: LegacyCourse[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * SubjectArea reference in Course (course-planner-service API)
 */
export interface SubjectAreaRef {
  name: string;
  CSSCCourse: number;
  SCEDSubject?: number;
}

export enum CourseStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  COMPLETE = 'COMPLETE',
  FAILED = 'FAILED',
  IN_PROGRESS = 'IN_PROGRESS',
}

/**
 * Course type matching course-planner-service API model.
 * Used by Course Catalog feature.
 */
export interface Course {
  id: string;
  name: string;
  subjectArea: SubjectAreaRef;
  credits: number;
  grades: number[];
  status: CourseStatus;
  schools: string[];
  cteCourse?: boolean;
  repeatable: boolean;
  instructionalLevel?: string;
  careerClusterCharacteristic?: string;
}

export interface CourseDetail extends Course {
  stateId?: string;
  /** PowerSchool / import style code: YR, SM, TR, QT, QM, TM, LS, IT, ST, OT, etc. */
  courseDuration?: string;
  description?: string;
  creditType?: string;
  elective?: boolean;
  stateCore?: boolean;
  techPrepCourse?: boolean;
  prerequisites?: string;
  coursePrerequisites?: Array<string[] | CourseSummary[]>;
  courseCorequisites?: Array<string[] | CourseSummary[]>;
  courseCreditRecovery?: Array<string[] | CourseSummary[]>;
  equivalentCourses?: Array<string[] | CourseSummary[]>;
  coursesRequireThis?: string[];
  studentGroupRestrictions?: string[];
  permissionRequired?: boolean;
  auditionRequired?: boolean;
  auditionDetails?: string;
  summerPrepRequired?: boolean;
  summerPrepDetails?: string;
  feeRequired?: boolean;
  feeDetails?: string;
}

export interface CourseSummary {
  id: string;
  /** Plan-scoped stable id from SIS/API when distinct from catalog `id`. */
  originalRecordId?: string;
  number?: string;
  identifier?: string;
  name?: string;
  credits?: number;
  creditsEarned?: number;
  status?: CourseStatus;
  repeatable?: boolean;
  courseGuid?: string;
  grades?: number[];
  inPlan?: boolean;
  plannedGrade?: number;
  /** Plan row disambiguator; PUT /requirements merges removals using this with `id` (see legacy `dehydrateCourse`). */
  statementUnique?: string;
  failedRecordIdentifier?: string;
  attemptedCredits?: number;
  alternatePriority?: number;
  /** Elective linkage from plan API (opaque). */
  electiveCourse?: unknown;
  subjectArea?: SubjectAreaRef;
  schools?: string[];
  instructionalLevel?: string;
  cteCourse?: boolean;
}

export interface CourseFilters {
  search?: string;
  status?: CourseStatus;
  grades?: string;
  /** Preferred: sent as a typed array for multi-select subject area filtering */
  subjectAreas?: string[];
  /** @deprecated Legacy comma-separated string; kept for backward compat with old callers */
  subjectArea?: string;
  /** Comma-separated school NIDs — matches CPS CourseQueryParams.schools (string) */
  schools?: string;
  page?: number;
  pageSize?: number;
  /** Server-side sort: field key (e.g. 'id', 'name'). Default 'id'. */
  sortBy?: string;
  /** Server-side sort direction. Default 'asc'. */
  sortOrder?: 'asc' | 'desc';
}

export interface PageInfo {
  total: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
}

export interface CoursesResponse extends PageInfo {
  data: Course[];
}

export interface CourseSection {
  id: string;
  courseId: string;
  sectionNumber: string;
  instructor: string;
  schedule: SectionSchedule;
  capacity: number;
  enrolled: number;
  term: Term;
  year: number;
}

export interface SectionSchedule {
  days: DayOfWeek[];
  startTime: string;
  endTime: string;
  location: string;
}

export type DayOfWeek =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

export type Term = 'fall' | 'spring' | 'summer' | 'winter';

export type CourseLevel = 'introductory' | 'intermediate' | 'advanced' | 'graduate';
